// Cahaya malam di atas selubung gelap (dipisah dari engine.js, perilaku identik):
// pendar lampu jalan ber-kap & sorot lampu depan/rem mobil.

// lamps: posisi kepala lampu (dari road.js saat build dunia); worldShift: geser/skala bitmap dunia saat gestur.
// Pendar dilukis SEKALI ke lapisan offscreen (warna pada intensitas penuh) saat lampu/kamera/ukuran berubah,
// lalu tiap frame hanya 1 drawImage dengan globalAlpha = atmos.dark — sebelumnya ratusan gradient per frame
// (penyebab frame berat di HP saat malam).
export function createLampGlow() {
  let layer = null, key = null;
  // Lapisan seluas bitmap dunia (layar + margin m) pada dpr 1 — pendar lembut tak butuh retina, hemat memori HP
  function build(lamps, zoom, W, H, m) {
    if (!layer) layer = document.createElement("canvas");
    const lw = W + 2 * m.x, lh = H + 2 * m.y;
    if (layer.width !== lw || layer.height !== lh) { layer.width = lw; layer.height = lh; }
    const ctx = layer.getContext("2d");
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, lw, lh);
    ctx.setTransform(1, 0, 0, 1, m.x, m.y);
    const z = zoom, pad = 40 * z;
    for (const lp of lamps) {
      if (lp.x < -m.x - pad || lp.x > W + m.x + pad || lp.y < -m.y - pad || lp.y > H + m.y + pad) continue; // di luar bitmap
      paintLamp(ctx, lp, z, 1);
    }
  }
  return {
    invalidate() { key = null; },   // dipanggil engine setiap dunia dibangun ulang (posisi lampu berubah)
    // zoom = zoom kamera SAAT dunia dibangun; m = margin bitmap dunia (posisi lampu mengikuti bitmap dunia + worldShift)
    draw(ctx, lamps, zoom, m, atmos, worldShift) {
      if (atmos.dark <= 0.15 || !lamps.length) return;
      const W = ctx.canvas.clientWidth, H = ctx.canvas.clientHeight;
      const k = `${W}|${H}|${m.x}|${m.y}`; // posisi/zoom lampu hanya berubah saat dunia dibangun ulang → engine memanggil invalidate()
      if (k !== key) { build(lamps, zoom, W, H, m); key = k; }
      ctx.save();
      ctx.globalAlpha = Math.min(1, atmos.dark);
      if (worldShift) { ctx.translate(worldShift.dx, worldShift.dy); ctx.scale(worldShift.s, worldShift.s); }
      ctx.drawImage(layer, -m.x, -m.y, W + 2 * m.x, H + 2 * m.y);
      ctx.restore();
    }
  };
}

// Satu lampu: LAYER 1 pantulan aspal → LAYER 2 bias pijar ke bawah → LAYER 3 kap penutup
// (digambar paling akhir agar menutupi bagian atas lampu; tidak ada halo besar). d = intensitas (0..1)
function paintLamp(ctx, lp, zoom, d) {
        // LAYER 1 — pantulan isometrik pipih (rasio 2:1) di permukaan aspal/trotoar.
        // Gambar elips via transform (bukan arc) agar radialGradient ikut ter-squash.
        if (lp.px != null) {
          const pr = 30 * zoom;
          ctx.save();
          ctx.translate(lp.px, lp.py);
          ctx.scale(1, 0.5); // elips isometrik pipih 2:1
          const pg = ctx.createRadialGradient(0, 0, 0, 0, 0, pr);
          pg.addColorStop(0, `rgba(255,220,130,${(0.42 * d).toFixed(3)})`);
          pg.addColorStop(1, "rgba(255,220,130,0)");
          ctx.fillStyle = pg;
          ctx.beginPath(); ctx.arc(0, 0, pr, 0, 7); ctx.fill();
          ctx.restore();
        }
        // LAYER 2 — bias pijar ke BAWAH: kerucut lembut dari celah kap ke aspal
        const bx = lp.x, by = lp.y + (lp.hh || 2) * 0.5 * zoom; // titik bawah kap
        const cone = 34 * zoom, cw = 7 * zoom;
        const cg = ctx.createLinearGradient(bx, by, bx, by + cone);
        cg.addColorStop(0, `rgba(255,230,150,${(0.18 * d).toFixed(3)})`);
        cg.addColorStop(1, "rgba(255,230,150,0)");
        ctx.fillStyle = cg;
        ctx.beginPath();
        ctx.moveTo(bx - cw, by);
        ctx.lineTo(bx + cw, by);
        ctx.lineTo(bx + cw * 1.9, by + cone);
        ctx.lineTo(bx - cw * 1.9, by + cone);
        ctx.closePath(); ctx.fill();
        // titik pijar pipih kecil mengintip di celah bawah kap
        ctx.fillStyle = `rgba(255,245,184,${(0.9 * d).toFixed(3)})`;
        ctx.beginPath(); ctx.ellipse(bx, by, Math.max(0.8, 2.2 * zoom), Math.max(0.5, 1.1 * zoom), 0, 0, 7); ctx.fill();
        // LAYER 3 — KAP penutup atas digambar paling akhir (menutupi bagian atas).
        // ukuran kap dari drawStreetLight di-zoom per-frame (hw/hh disimpan unzoomed)
        const hw2 = (lp.hw || 2.4) * zoom, hh2 = (lp.hh || 2.4) * zoom;
        ctx.fillStyle = "#2B303A";
        ctx.beginPath();
        ctx.ellipse(lp.x, lp.y, hw2, hh2, 0, Math.PI, 0);
        ctx.closePath(); ctx.fill();
}

// headlights: {gx,gy,x,y,dx,dy,a,half,brake} per mobil (dikumpulkan traffic.js tiap frame, urut kedalaman).
// occl (opsional): lapisan oklusi engine — sorot digambar ke scratch lalu siluet bangunan DI DEPAN mobil
// dihapus (seperti mover), sehingga cahaya tidak menembus atap/dinding gedung; satu blit di akhir.
export function drawHeadlights(ctx, headlights, cam, atmos, occl, W, H) {
    if (atmos.dark > 0.15 && headlights.length) {
      const ni = (atmos.dark - 0.15) / 0.85;
      const r = 190 * cam.zoom;   // jangkauan sorot + kolam cahaya di ujungnya
      if (occl) occl.beginMoverLayer();
      for (const h of headlights) {
        const a = ni * h.a;
        if (a <= 0.02) continue;
        if (occl) occl.drawOccluded(h.gx, h.gy, r, g => drawOneHeadlight(g, h, cam.zoom, a));
        else drawOneHeadlight(ctx, h, cam.zoom, a);
      }
      if (occl) occl.endMoverLayer(W, H);
    }
}

function drawOneHeadlight(ctx, h, z, a) {
    // Sorot lampu depan mobil saat gelap (di atas selubung malam, seperti lampu jalan).
    // Intensitas ikut atmos.dark → menyala otomatis & bertahap saat senja/malam tiba,
    // dan ikut memudar bersama fade in/out mobil (h.a = alpha mobil).
        // arah hadap mobil di ruang layar (grid → iso)
        let ux = (h.dx - h.dy), uy = (h.dx + h.dy) * 0.5;
        const n0 = Math.hypot(ux, uy) || 1; ux /= n0; uy /= n0;
        const px = -uy, py = ux; // tegak lurus arah hadap
        const halfPx = (h.half || 0.35) * 71.5 * z; // setengah panjang mobil di layar
        const fx = h.x + ux * halfPx, fy = h.y + uy * halfPx; // ujung depan mobil
        const L = 120 * z, w0 = 5 * z, w1 = 26 * z;
        const ex = fx + ux * L, ey = fy + uy * L;
        // kerucut sorot (meredup sepanjang jarak)
        const grad = ctx.createLinearGradient(fx, fy, ex, ey);
        grad.addColorStop(0, `rgba(255,244,190,${(0.5 * a).toFixed(3)})`);
        grad.addColorStop(1, "rgba(255,244,190,0)");
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.moveTo(fx + px * w0, fy + py * w0);
        ctx.lineTo(ex + px * w1, ey + py * w1);
        ctx.lineTo(ex - px * w1, ey - py * w1);
        ctx.lineTo(fx - px * w0, fy - py * w0);
        ctx.closePath(); ctx.fill();
        // kolam cahaya di ujung sorot
        const pool = ctx.createRadialGradient(ex, ey, 0, ex, ey, 28 * z);
        pool.addColorStop(0, `rgba(255,240,180,${(0.3 * a).toFixed(3)})`);
        pool.addColorStop(1, "rgba(255,240,180,0)");
        ctx.fillStyle = pool;
        ctx.beginPath(); ctx.ellipse(ex, ey, 28 * z, 14 * z, 0, 0, 7); ctx.fill();
        // dua titik lampu depan yang terang
        ctx.fillStyle = `rgba(255,250,222,${(0.95 * a).toFixed(3)})`;
        const hd = 5 * z, rr = Math.max(1, 1.8 * z);
        ctx.beginPath(); ctx.arc(fx + px * hd, fy + py * hd - 2 * z, rr, 0, 7); ctx.fill();
        ctx.beginPath(); ctx.arc(fx - px * hd, fy - py * hd - 2 * z, rr, 0, 7); ctx.fill();
        // lampu rem merah di buritan saat mengerem/berhenti
        if (h.brake) {
          const bx = h.x - ux * halfPx, by = h.y - uy * halfPx;
          const bg = ctx.createRadialGradient(bx, by, 0, bx, by, 14 * z);
          bg.addColorStop(0, `rgba(255,60,40,${(0.35 * a).toFixed(3)})`);
          bg.addColorStop(1, "rgba(255,60,40,0)");
          ctx.fillStyle = bg;
          ctx.beginPath(); ctx.ellipse(bx, by, 14 * z, 7 * z, 0, 0, 7); ctx.fill();
          ctx.fillStyle = `rgba(255,70,50,${(0.95 * a).toFixed(3)})`;
          ctx.beginPath(); ctx.arc(bx + px * hd, by + py * hd - 2 * z, rr, 0, 7); ctx.fill();
          ctx.beginPath(); ctx.arc(bx - px * hd, by - py * hd - 2 * z, rr, 0, 7); ctx.fill();
        }
}
